import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Building2,
    Plus,
    Search,
    Building,
    Trash2,
    FileEdit,
    ToggleLeft,
    ToggleRight,
    AlertCircle,
    RefreshCw,
    Settings2
} from 'lucide-react';
import BotonAcceso from "../elementos/BotonAcceso.jsx";
import ConfigSedeTipos from "../Modal/ConfigSedeTipos.jsx";
import { useTheme } from "../guardarToken/themeContext";
import { CircularProgress, Tooltip } from "@mui/material";

// Fallback robusto por si el .env no se cargó correctamente
const apiUrlSedes = process.env.REACT_APP_API_SEDES || '/api/sedes/';

function AbmSedes() {
    const [sedes, setSedes] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSedeConfig, setSelectedSedeConfig] = useState(null);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    useEffect(() => {
        getSedes();
    }, []);

    const getSedes = async () => {
        try {
            setLoading(true);
            setError(null);

            // Verificamos conectividad
            const res = await axios.get(apiUrlSedes);

            if (Array.isArray(res.data)) {
                setSedes(res.data);
            } else {
                console.error("Respuesta inesperada de la API:", res.data);
                setError("La respuesta del servidor no es válida.");
            }
        } catch (error) {
            console.error("Error al intentar mostrar sedes:", error);
            setError(
                error.response?.data?.message ||
                "No se pudo conectar con el servidor. Verifique que el backend esté en funcionamiento."
            );
        } finally {
            setLoading(false);
        }
    };

    const deleteSede = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar esta sede? Se eliminarán todas las direcciones y sectores asociados (EFECTO CASCADA).")) return;
        try {
            await axios.delete(`${apiUrlSedes}${id}`);
            getSedes();
        } catch (error) {
            console.error("Error al intentar eliminar sede:", error);
            alert("No se pudo eliminar la sede: " + (error.response?.data?.message || error.message));
        }
    };

    const toggleStatus = async (id) => {
        try {
            await axios.patch(`${apiUrlSedes}${id}/toggle`);
            getSedes();
        } catch (error) {
            console.error("Error al cambiar estado de sede:", error);
        }
    };

    const filteredSedes = sedes.filter((sede) => {
        const search = searchTerm.toLowerCase();
        return (
            sede.nombre?.toLowerCase().includes(search) ||
            sede.codigo?.toLowerCase().includes(search)
        );
    });

    if (loading) {
        return (
            <div className={`flex justify-center items-center h-screen ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
                <CircularProgress color="primary" />
            </div>
        );
    }

    return (
        <div className={`min-h-screen p-3 lg:p-4 font-sans transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-800"}`}>

            {/* HEADER PREMIUM */}
            <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm p-5 mb-6 flex flex-col md:flex-row items-center justify-between border ring-1 ring-black/5 animate-in slide-in-from-top duration-700`}>
                <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-3 rounded-xl shadow-lg shadow-blue-900/20">
                        <Building2 className="text-white w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black tracking-tight leading-none mb-1">Gestión de Sedes</h1>
                        <p className={`text-[11px] ${isDarkMode ? "text-slate-400" : "text-slate-500"} font-black uppercase tracking-wider`}>Entidad principal: Sedes y Unidades Operativas</p>
                    </div>
                </div>

                <div className="flex items-center gap-3 mt-4 md:mt-0">
                    <div className="relative w-48 md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Buscar sede..."
                            style={{ paddingLeft: '48px' }}
                            className={`w-full pr-4 py-2 rounded-xl text-xs font-bold focus:outline-none transition-all ${isDarkMode ? "bg-slate-950 border-slate-800 text-white focus:border-blue-500/50" : "bg-slate-50 border-slate-200 text-slate-800 focus:border-blue-500"
                                } border`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <BotonAcceso
                        variant="accent"
                        onClick={() => navigate('/admin/CreateSede')}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-black text-[11px] uppercase tracking-widest px-5 py-2.5 rounded-xl shadow-lg shadow-blue-950/20 flex items-center gap-2 transform hover:scale-[1.02] transition-all"
                    >
                        <Plus className="w-4 h-4" /> Nueva Sede
                    </BotonAcceso>
                </div>
            </div>

            {/* ERROR DISPLAY */}
            {error && (
                <div className="mb-6 animate-in fade-in zoom-in duration-300">
                    <div className={`${isDarkMode ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-100"} border p-4 rounded-2xl flex flex-col items-center text-center gap-3`}>
                        <AlertCircle className="w-10 h-10 text-red-500" />
                        <div>
                            <h3 className="text-sm font-black text-red-600 uppercase tracking-widest mb-1">Error de Conexión</h3>
                            <p className="text-xs font-bold text-red-500/70">{error}</p>
                        </div>
                        <button
                            onClick={getSedes}
                            className={`mt-2 flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-tighter ${isDarkMode ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-600"}`}
                        >
                            <RefreshCw className="w-3 h-3" /> Reintentar
                        </button>
                    </div>
                </div>
            )}

            {/* CONTENT AREA */}
            {!error && (
                <div className="grid grid-cols-1 gap-6 animate-in slide-in-from-bottom duration-1000">
                    <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm border overflow-hidden`}>
                        <table className="w-full text-left">
                            <thead>
                                <tr className={`${isDarkMode ? "bg-black/20 text-slate-500 border-slate-800" : "bg-slate-50 text-slate-400 border-slate-100"} border-b text-[10px] font-black uppercase tracking-[0.15em]`}>
                                    <th className="px-6 py-4">Sede / Código</th>
                                    <th className="px-6 py-4">Estado</th>
                                    <th className="px-6 py-4 text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? "divide-slate-800" : "divide-slate-100"}`}>
                                {filteredSedes.map((item) => (
                                    <tr key={item.id} className="group transition-all hover:bg-blue-500/[0.02]">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${isDarkMode ? "bg-slate-800 text-blue-400" : "bg-blue-50 text-blue-600"}`}>
                                                    <Building className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <span className="text-sm font-black tracking-tight block" style={{ color: isDarkMode ? '#60a5fa' : '#1e3a8a' }}>
                                                        {item.nombre}
                                                    </span>
                                                    <span className="text-[10px] font-bold text-slate-500 uppercase">{item.codigo}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => toggleStatus(item.id)}
                                                className="flex items-center gap-2"
                                            >
                                                {item.activo ? (
                                                    <span className="flex items-center gap-1.5 text-[10px] font-extrabold text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded-lg uppercase tracking-wider">
                                                        <ToggleRight className="w-4 h-4" /> Activo
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1.5 text-[10px] font-extrabold text-slate-400 bg-slate-400/10 px-2 py-1 rounded-lg uppercase tracking-wider">
                                                        <ToggleLeft className="w-4 h-4" /> Inactivo
                                                    </span>
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center gap-2">
                                                <Tooltip title="Editar Sede">
                                                    <button
                                                        onClick={() => navigate(`/admin/EditSede/${item.id}`)}
                                                        className={`p-2 rounded-xl border transition-all ${isDarkMode
                                                            ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-blue-500/50"
                                                            : "bg-white border-slate-200 text-slate-400 hover:text-blue-600 hover:border-blue-200 shadow-sm"
                                                            }`}
                                                    >
                                                        <FileEdit className="w-4 h-4" />
                                                    </button>
                                                </Tooltip>
                                                <Tooltip title="Eliminar Sede">
                                                    <button
                                                        onClick={() => deleteSede(item.id)}
                                                        className={`p-2 rounded-xl border transition-all ${isDarkMode
                                                            ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-red-500 hover:border-red-500/50"
                                                            : "bg-white border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 shadow-sm"
                                                            }`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </Tooltip>
                                                <Tooltip title="Configurar Menús">
                                                    <button
                                                        onClick={() => { setSelectedSedeConfig(item); setIsConfigModalOpen(true); }}
                                                        className={`p-2 rounded-xl border transition-all ${isDarkMode
                                                            ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-blue-400 hover:border-blue-500/50"
                                                            : "bg-white border-slate-200 text-slate-400 hover:text-blue-600 hover:border-blue-200 shadow-sm"
                                                            }`}
                                                    >
                                                        <Settings2 className="w-4 h-4" />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredSedes.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-20 grayscale opacity-40">
                                <Building2 className="w-12 h-12 mb-3 text-slate-400" />
                                <p className="text-xs font-black uppercase tracking-widest text-slate-400">No se encontraron sedes</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ConfigSedeTipos
                isOpen={isConfigModalOpen}
                onClose={() => setIsConfigModalOpen(false)}
                sede={selectedSedeConfig}
            />
        </div>
    );
}

export default AbmSedes;
